import React, { useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { OccupancyMonthlyReportResponseV4 } from "../../../../services/cm-group-report/response/OccupancyMonthlyReportResponseV4";
import { OccupancyYearlyReportResponseV4 } from "../../../../services/cm-group-report/response/OccupancyYearlyReportResponseV4";
import { useAuth } from "../../../../auth/context/AuthProvider";

interface OccupancyReportTableV4Props {
    monthlyData: OccupancyMonthlyReportResponseV4;
    yearlyData: OccupancyYearlyReportResponseV4;
    monthlyDataByMonth: OccupancyMonthlyReportResponseV4;
    yearlyDataByYear: OccupancyYearlyReportResponseV4;
}

const MONTHS_NUMERIC = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MONTH_NAMES = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
const COLUMNS = ["Property", "Report", ...MONTH_NAMES, "Total"];

const OccupancyReportTableV4: React.FC<OccupancyReportTableV4Props> = ({ monthlyData, yearlyData, monthlyDataByMonth, yearlyDataByYear }) => {
    const { user } = useAuth();

    const formatOccupancy = (value?: string | null) => {
        if (value && parseFloat(value) > 0) {
            return `${value}%`;
        }
        return '-';
    };

    // Pivot monthly data by property
    const pivotedData = useMemo(() => {
        const pivot: { [propertyId: string]: { [month: string]: string } } = {};
        (monthlyData?.data || []).forEach((item: any) => {
            const propId = item.property_id || 'unknown';
            const month = item.month?.split('-')[1]; // Extract month from YYYY-MM
            if (!pivot[propId]) pivot[propId] = {};
            pivot[propId][month] = item.occupancy;
        });
        return pivot;
    }, [monthlyData]);

    // Total per month from monthlyDataByMonth
    const totalMonthlyMap = useMemo(() => new Map(
        (monthlyDataByMonth?.data || []).map((item: any) => [item.month?.split('-')[1], item.occupancy])
    ), [monthlyDataByMonth]);

    // Yearly total per property from yearlyData
    const yearlyDataMap = useMemo(() => new Map(
        (yearlyData?.data || []).map((item: any) => [item.property_id, item.occupancy])
    ), [yearlyData]);

    // Grand total from yearlyDataByYear
    const grandTotalYearly = (yearlyDataByYear?.data || [])[0]?.occupancy || '-';

    const propertyIds = Object.keys(pivotedData);

    return (
        <div className="mt-4">
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => <th key={col}>{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {propertyIds.length > 0 ? (
                            propertyIds.map((propertyId) => {
                                const propertyName = user?.properties?.[propertyId] || propertyId;
                                const monthlyOccupancies = MONTHS_NUMERIC.map(month => formatOccupancy(pivotedData[propertyId][month]));
                                const yearlyTotal = formatOccupancy(yearlyDataMap.get(propertyId));
                                return (
                                    <tr key={propertyId}>
                                        <td>{propertyName}</td>
                                        <td>Occupancy</td>
                                        {monthlyOccupancies.map((occ, idx) => (
                                            <td key={idx}>{occ}</td>
                                        ))}
                                        <td>{yearlyTotal}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={COLUMNS.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Property</strong></td>
                            <td><strong>Occupancy</strong></td>
                            {MONTHS_NUMERIC.map(month => (
                                <td key={month}><strong>{formatOccupancy(totalMonthlyMap.get(month))}</strong></td>
                            ))}
                            <td><strong>{formatOccupancy(grandTotalYearly)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
}

export default OccupancyReportTableV4;